import React, { useContext, useState, useEffect } from 'react'
import cn from 'classnames'
import { FixedSpacer, Typography } from '@grammarly/badger-ui'
import { SocialShareWidget } from '@components/share-widget/social-share-widget'
import { CtaButtonDescription } from '@components/item/cta-button'
import { ContentProps, ContentRenderer } from '@components/renderer'
import { renderRichTextMultiLineContent } from '@components/renderer/rich-text'
import { SectionContext } from '@contexts/section-context'

import { useGetAuthAndDeviceTypeCta } from '@lib/hooks/auth-and-device-type-cta'
import { TypeSectionHero, SectionHeroLayoutDir } from '@lib/types'
import { isUnresolvedLink } from '@lib/utils/contentful-entry'
import { isTypeComponentEmptyStub } from '@lib/types/content-types'
import { getDescriptionComponent } from './title'

import heroStyles from './hero.module.scss'
import btsStyles from '@components/dynamic/bts-agents-25/bts-agents-25.module.scss'

export const KeychainHeroTitle: React.FC<ContentProps<TypeSectionHero>> = (props) => {
  const {
    structure: {
      fields: { layoutDirection, title: _title, titleSize, description, ctAs, socialShareWidget },
    },
  } = props

  const { isH1Section } = useContext(SectionContext)

  const { getAuthAndDeviceTypeCta } = useGetAuthAndDeviceTypeCta()

  // Animation state management
  const [animationStates, setAnimationStates] = useState({
    seoTitle: false,
    titleLine1: false,
    titleLine2: false,
    description: false,
    ctas: false,
    socialShare: false,
  })

  useEffect(() => {
    // Start animations immediately - no dependency on Spline loading
    const timeouts = [
      setTimeout(() => setAnimationStates((prev) => ({ ...prev, seoTitle: true })), 50),
      setTimeout(() => setAnimationStates((prev) => ({ ...prev, titleLine1: true })), 300),
      setTimeout(() => setAnimationStates((prev) => ({ ...prev, titleLine2: true })), 450),
      setTimeout(() => setAnimationStates((prev) => ({ ...prev, description: true })), 600),
      setTimeout(() => setAnimationStates((prev) => ({ ...prev, ctas: true })), 900),
      setTimeout(() => setAnimationStates((prev) => ({ ...prev, socialShare: true })), 1200),
    ]

    // Cleanup timeouts on unmount
    return () => {
      timeouts.forEach(clearTimeout)
    }
  }, [])

  const TitleComponent = Typography.T1
  const DescriptionComponent = getDescriptionComponent(titleSize)

  return (
    <figcaption>
      <header>
        {/* Keychain Eyebrow with Badge */}

        <TitleComponent as={isH1Section ? 'h1' : 'h2'} className={btsStyles.btsHeroTitle}>
          <span
            className={cn(btsStyles.btsTitleContent, {
              [btsStyles.animate]: animationStates.titleLine1,
            })}
          >
            Back-to-school
          </span>
          <span
            className={cn(btsStyles.btsTitleContent, {
              [btsStyles.animate]: animationStates.titleLine2,
            })}
          >
            swag, secured.
          </span>
        </TitleComponent>

        <FixedSpacer multiplier={3} />
        <DescriptionComponent
          as="div"
          className={cn(btsStyles.btsHeroDescription, { [btsStyles.fadeIn]: animationStates.description })}
        >
          {renderRichTextMultiLineContent(description)}
        </DescriptionComponent>
      </header>
      {ctAs?.length && (
        <>
          <FixedSpacer multiplier={4} />
          <div
            className={cn(heroStyles.ctasContainer, btsStyles.hiddenBeforeAnimate, {
              [heroStyles.ctasCentered]:
                layoutDirection === SectionHeroLayoutDir.Vertical ||
                layoutDirection === SectionHeroLayoutDir.VerticalWide,
              [btsStyles.fadeIn]: animationStates.ctas,
            })}
          >
            <ContentRenderer structure={ctAs} />
          </div>
          <div
            className={cn(heroStyles.ctasDescriptionsContainer, btsStyles.hiddenBeforeAnimate, {
              [btsStyles.fadeIn]: animationStates.ctas,
            })}
          >
            {ctAs
              .map(getAuthAndDeviceTypeCta)
              .filter((cta) => !isUnresolvedLink(cta))
              .filter((cta) => !isTypeComponentEmptyStub(cta))
              .map((cta) => (
                <CtaButtonDescription key={`description-${cta.sys.contentType.sys.id}-${cta.sys.id}`} structure={cta} />
              ))}
          </div>
        </>
      )}
      {socialShareWidget && (
        <>
          <FixedSpacer multiplier={5} />
          <div className={cn(btsStyles.hiddenBeforeAnimate, { [btsStyles.fadeIn]: animationStates.socialShare })}>
            <SocialShareWidget structure={socialShareWidget} />
          </div>
        </>
      )}
    </figcaption>
  )
}
